/**
 * Order and visibility of the blocks under leftover on Overview / Home.
 *
 * Leftover itself is not in this list. It is the job of the screen, so it
 * stays first. Device-local, like theme — the phone keeps its own order.
 */

export const HOME_SECTIONS = ['plan', 'needs', 'pinned', 'paycheck'] as const;

export type HomeSection = (typeof HOME_SECTIONS)[number];

export interface HomeLayout {
  order: HomeSection[];
  hidden: HomeSection[];
}

export const HOME_SECTION_META: Record<
  HomeSection,
  { title: string; subtitle: string }
> = {
  plan: { title: 'Envelopes & Bills', subtitle: 'What is left in envelopes and bills' },
  needs: { title: 'Needs you', subtitle: 'Sorting, holds, alerts, overspent' },
  pinned: { title: 'Pinned', subtitle: 'Envelopes and bills you keep nearby' },
  paycheck: { title: 'Paycheck', subtitle: 'How this paycheck is assigned' },
};

export const DEFAULT_HOME_LAYOUT: HomeLayout = {
  order: [...HOME_SECTIONS],
  hidden: [],
};

export const HOME_LAYOUT_STORAGE_KEY = 'alohamula.homeLayout';

function isSection(value: unknown): value is HomeSection {
  return typeof value === 'string' && (HOME_SECTIONS as readonly string[]).includes(value);
}

/** Older builds stored envelopes and bills as two blocks. */
function mappedSection(value: unknown): unknown {
  return value === 'envelopes' || value === 'bills' ? 'plan' : value;
}

export function normalizeHomeOrder(raw: unknown): HomeSection[] {
  const seen = new Set<HomeSection>();
  const order: HomeSection[] = [];
  if (Array.isArray(raw)) {
    for (const value of raw) {
      const mapped = mappedSection(value);
      if (!isSection(mapped) || seen.has(mapped)) continue;
      seen.add(mapped);
      order.push(mapped);
    }
  }
  for (const section of HOME_SECTIONS) {
    if (!seen.has(section)) order.push(section);
  }
  return order;
}

export function normalizeHomeHidden(raw: unknown): HomeSection[] {
  const seen = new Set<HomeSection>();
  const hidden: HomeSection[] = [];
  if (!Array.isArray(raw)) return hidden;
  for (const value of raw) {
    const mapped = mappedSection(value);
    if (!isSection(mapped) || seen.has(mapped)) continue;
    seen.add(mapped);
    hidden.push(mapped);
  }
  return hidden;
}

export function normalizeHomeLayout(raw: unknown): HomeLayout {
  if (Array.isArray(raw)) {
    return { order: normalizeHomeOrder(raw), hidden: [] };
  }
  if (raw && typeof raw === 'object') {
    const record = raw as { order?: unknown; hidden?: unknown };
    return {
      order: normalizeHomeOrder(record.order),
      hidden: normalizeHomeHidden(record.hidden),
    };
  }
  return { order: [...HOME_SECTIONS], hidden: [] };
}

export function visibleHomeOrder(layout: HomeLayout): HomeSection[] {
  const hidden = new Set(layout.hidden);
  return layout.order.filter((section) => !hidden.has(section));
}

export function readHomeLayout(): HomeLayout {
  try {
    const stored = window.localStorage.getItem(HOME_LAYOUT_STORAGE_KEY);
    if (!stored) return { order: [...HOME_SECTIONS], hidden: [] };
    return normalizeHomeLayout(JSON.parse(stored));
  } catch {
    return { order: [...HOME_SECTIONS], hidden: [] };
  }
}

export function writeHomeLayout(layout: HomeLayout): HomeLayout {
  const next = normalizeHomeLayout(layout);
  try {
    window.localStorage.setItem(HOME_LAYOUT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* Preference won't survive a reload; the page still reorders now. */
  }
  return next;
}
